<script setup lang="ts">
/**
 * This view is used to display a list of urls that belong to a team or an organization. The view contains a list of
 * urls that have been passed in as a prop as well as some controls allowing you to search and filter through the
 * urls.
 */
import { computed, ref, watch } from "vue";
import { ButtonSize, isNullish, TuButton, TuFeedbackBanner } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints, useInfiniteScroll } from "@vueuse/core";

import { logError, logWarning } from "@Resources/js/helpers/logger.js";
import { TeamUrlViewModel, FetchOptions } from "@Modules/teams/ViewModels/TeamUrlViewModel";
import V2UrlFilter from "@Modules/urls/Components/V2UrlFilter.vue";
import UrlList from "@Modules/urls/Components/UrlList/UrlList.vue";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory.ts";
import type { ReactivePaginator } from "@Resources/js/api/ReactivePaginator.ts";
import { useTranslation } from "@Resources/js/composables";
import { CustomUrlsErrors } from "@Modules/urls/Api/UrlsApi";

//
// Props
interface Props {
    canViewUrls: boolean,
    loading: boolean,
    paginator: ReactivePaginator<UrlAlias>,
    fetch: TeamUrlViewModel["fetch"],
    downloadUrls: TeamUrlViewModel["downloadUrls"],
    errors?: Record<string, string>,
    selected?: string | null,
    teamIdentifier?: string,
    filter?: FetchOptions["filter"],
    searchType?: FetchOptions["searchType"],
    search?: string,
}
const props = withDefaults(defineProps<Props>(), {
    errors: () => ({}),
    selected: null,
    teamIdentifier: "",
    filter: "available",
    searchType: "alias",
    search: "",
});

interface Events {
  (e: "view-all-analytics"): void;
  (e: "url-selected"): void;
}
const emit = defineEmits<Events>();

//
// Data
const scrollContainer = ref<HTMLElement | null>(null);

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Computed
const urls = computed<UrlAlias[]>(() => props.paginator.page.data);
const hasMore = computed<boolean>(() => props.paginator.hasNext.value);
const hasFetchError = computed(() =>
  !isNullish(Object.keys(props.errors).find((key) => key === CustomUrlsErrors.FETCH_FAIL))
  && !props.loading,
);

const canLoadMore = computed(() => hasMore.value && !hasFetchError.value && !props.loading);

/**
 * A composable that allows us to reload the page when the user scrolls to the bottom
 */
const { reset } = useInfiniteScroll(
    scrollContainer,
    async () => {
        if (!canLoadMore.value) {
            return;
        }
        await props.paginator.next({ clear: false });
    },
    {
        distance: 100,
        canLoadMore: () => canLoadMore.value,
    },
);


//
// Watchers
watch(() => props.teamIdentifier, () => {
    reset();
});

//
// Methods
const fetchUrls = async ({ filter = "available", searchType = "alias", search = "" }: FetchOptions) => {
  if (props.loading) {
    logWarning("Cannot fetch URLs while a request is still in progress!");

    return;
  }

  await props.fetch({ clear: true, filter, searchType, search });
};

/**
 * Wrapper for downloadUrls that prevents download when loading or no URLs available
 */
function handleDownload(): void {
    if (props.loading) {
        return;
    }

    if (urls.value.length === 0) {
        return;
    }

    props.downloadUrls();
}

function onRetryUrlError() {
  const isListEmpty = urls.value.length === 0;

  if (isListEmpty) {
    fetchUrls({ filter: props.filter, searchType: props.searchType, search: props.search })
      .catch(() => { logError("[onRetryUrlError]: Couldn't fetch initial URls."); });

    return;
  }

  props.paginator.next({ clear: false })
    .catch(() => { logError("[onRetryUrlError]: Couldn't fetch next URls."); });
}

function onTagClick(tagName: string): void {
  if (props.searchType === "tag" && props.search === tagName) {
    return;
  }

  props.fetch({ clear: true, filter: props.filter, searchType: "tag", search: tagName });
}
</script>

<template>
  <div
    data-test-id="dashboard_sidebar"
    class="url-panel"
    :class="{ 'url-panel--mobile': isMobileView }"
  >
    <template v-if="canViewUrls">
      <V2UrlFilter
        class="_urlFilter url-panel__filter"
        data-test-id="dashboard_sidebar_filter"
        :filter="filter"
        :search-type="searchType"
        :search="search"
        @search="fetchUrls"
        @download="handleDownload"
      />

      <div class="url-panel__button-wrapper">
        <TuButton
          class="_all-analytics-button url-panel__all-button"
          is-block
          label="View All Analytics"
          :size="ButtonSize.MEDIUM"
          @click="emit('view-all-analytics')"
        />
      </div>

      <UrlList
        ref="scrollContainer"
        class="_url_list url-panel__list"
        :items="urls"
        :loading="loading"
        :selected="selected"
        @url-selected="emit('url-selected')"
        @tag="onTagClick"
      />

      <div class="url-panel__wrapper-footer">
        <p
          v-if="loading"
          class="_loading url-panel__loading"
        >
          <i class="url-panel__loading-icon fa-solid fa-spinner" /> {{ __("organization_dashboard.url_list.loading") }}
        </p>
        <TuFeedbackBanner
          v-if="hasFetchError"
          class="_error_banner url-panel__feedback-banner"
          :content="__('organization_dashboard.url_list.error_message')"
          :feedback-button="{ 'label': __('organization_dashboard.url_list.error_button'), 'iconClass': 'fa-solid fa-arrows-rotate' }"
          :is-closable="false"
          @cta-click="onRetryUrlError"
        />
      </div>
    </template>
    <template v-if="!canViewUrls">
      You must have permission to view these urls
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/tools/animation.scss" as animation;

.url-panel {
  $self: &;

  display: flex;
  flex-flow: column;
  height: 100%;

  background-color: color.$background-overlay;

  &__feedback-banner {
    background-color: color.$alert;
  }

  &__button-wrapper {
    padding: spacing.$xxxs spacing.$s;
  }

  &__list {
    overflow-y: auto;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: spacing.$xs-5;
    padding: spacing.$xxxs spacing.$s;
    margin: 0;

    text-align: center;
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-medium;

    border-top: border.$primary;
    border-top-color: color.$background-secondary;
    background-color: color.$background-overlay;
  }

  &__loading-icon {
    @include animation.rotate;
  }

  &--mobile {
    #{ $self }__button-wrapper {
      padding: spacing.$xxxs;
    }
  }
}
</style>
